import { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, RotateCcw, AlertTriangle, Server } from "lucide-react";
import { toast } from "sonner";

import {
  cleverLmsService,
  isCleverLmsError,
  type AssignmentFailure,
  type CleverDistrict,
  type SubmissionFailure,
  type UnresolvedRosterEntry,
} from "../services/clever-lms.service";
import { cn } from "../lib/utils";
import {
  describeLmsState,
  describeRosterReason,
  formatMatchedPercentage,
  formatPayload,
  formatTimestamp,
  truncateError,
} from "./clever-lms/formatters";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

type TabValue =
  | "districts"
  | "roster-issues"
  | "publish-failures"
  | "gradeback-failures";

type LoadState<T> =
  | { status: "loading" }
  | { status: "ready"; data: T }
  | { status: "empty" }
  | { status: "unavailable-503" }
  | { status: "not-deployed-404" }
  | { status: "error"; message: string };

const INITIAL_STATE: LoadState<never> = { status: "loading" };

function classifyError(error: unknown): LoadState<never> {
  if (isCleverLmsError(error)) {
    if (error.status === 503) return { status: "unavailable-503" };
    if (error.status === 404) return { status: "not-deployed-404" };
    return {
      status: "error",
      message: error.detail || error.message || "Clever LMS request failed",
    };
  }
  const message =
    error instanceof Error ? error.message : "Unexpected error";
  return { status: "error", message };
}

interface EmptyShellProps {
  title: string;
  description: string;
  icon?: React.ReactNode;
}

function EmptyShell({ title, description, icon }: EmptyShellProps) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border/70 bg-muted/20 py-12 text-center">
      {icon ?? <Server className="size-5 text-muted-foreground" />}
      <p className="text-sm font-medium text-foreground">{title}</p>
      <p className="max-w-md text-xs text-muted-foreground">{description}</p>
    </div>
  );
}

interface SectionStateProps<T> {
  state: LoadState<T>;
  emptyTitle: string;
  emptyDescription?: string;
  children: (data: T) => React.ReactNode;
}

function SectionState<T>({
  state,
  emptyTitle,
  emptyDescription,
  children,
}: SectionStateProps<T>) {
  if (state.status === "loading") {
    return (
      <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (state.status === "unavailable-503") {
    return (
      <EmptyShell
        icon={<AlertTriangle className="size-5 text-amber-600" />}
        title="Clever LMS Connect is not configured in this environment"
        description="Set CLEVER_API_BEARER_TOKEN on the ai-tutor backend to enable these diagnostics."
      />
    );
  }
  if (state.status === "not-deployed-404") {
    return (
      <EmptyShell
        icon={<AlertTriangle className="size-5 text-amber-600" />}
        title="Diagnostics endpoints not yet deployed (ENGG-1766)"
        description="These endpoints will appear once the ai-tutor ENGG-1766 PR has been merged and deployed."
      />
    );
  }
  if (state.status === "error") {
    return (
      <EmptyShell
        icon={<AlertTriangle className="size-5 text-red-600" />}
        title="Failed to load data"
        description={state.message}
      />
    );
  }
  if (state.status === "empty") {
    return (
      <EmptyShell
        title={emptyTitle}
        description={emptyDescription ?? "Nothing to show yet."}
      />
    );
  }
  return <>{children(state.data)}</>;
}

function normalizeListState<T>(
  items: T[],
): LoadState<T[]> {
  if (!items || items.length === 0) {
    return { status: "empty" };
  }
  return { status: "ready", data: items };
}

// ---------------------------------------------------------------------------
// Districts
// ---------------------------------------------------------------------------

interface DistrictsTabProps {
  state: LoadState<CleverDistrict[]>;
  onRefreshDistrict: (id: string) => void;
  onTriggerSync: (id: string) => void;
  refreshingId: string | null;
  syncingId: string | null;
}

function DistrictsTab({
  state,
  onRefreshDistrict,
  onTriggerSync,
  refreshingId,
  syncingId,
}: DistrictsTabProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Connected districts</CardTitle>
        <CardDescription>
          Clever-linked districts, last sync health and quick actions.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <SectionState
          state={state}
          emptyTitle="No districts connected yet"
          emptyDescription="Districts will appear here once a Clever OAuth link has been completed."
        >
          {(districts) => (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>District</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Matched</TableHead>
                  <TableHead>Last sync</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {districts.map((district) => {
                  const appearance = describeLmsState(district.lms_state);
                  return (
                    <TableRow key={district.clever_district_id}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium text-foreground">
                            {district.name || district.clever_district_id}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {district.clever_district_id}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={appearance.variant}
                          className={cn("capitalize", appearance.className)}
                        >
                          {appearance.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {formatMatchedPercentage(
                          district.lms_matched_percentage,
                        )}
                      </TableCell>
                      <TableCell>
                        {formatTimestamp(district.last_lms_sync)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              onRefreshDistrict(district.clever_district_id)
                            }
                            disabled={
                              refreshingId === district.clever_district_id
                            }
                          >
                            <RefreshCw
                              className={cn(
                                "size-4",
                                refreshingId === district.clever_district_id &&
                                  "animate-spin",
                              )}
                            />
                            Refresh from Clever
                          </Button>
                          <Button
                            size="sm"
                            onClick={() =>
                              onTriggerSync(district.clever_district_id)
                            }
                            disabled={syncingId === district.clever_district_id}
                          >
                            <RotateCcw className="size-4" />
                            Trigger Roster Sync
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </SectionState>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Roster issues
// ---------------------------------------------------------------------------

function RosterIssuesTab({
  state,
}: {
  state: LoadState<UnresolvedRosterEntry[]>;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Unresolved roster mappings</CardTitle>
        <CardDescription>
          Clever users/sections that could not be matched to EverTutor records.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <SectionState
          state={state}
          emptyTitle="All roster mappings resolved"
          emptyDescription="No Clever users or sections are currently unresolved."
        >
          {(entries) => (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Clever ID</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Detail</TableHead>
                  <TableHead>Last seen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry, idx) => (
                  <TableRow key={`${entry.clever_id}-${idx}`}>
                    <TableCell className="font-mono text-xs">
                      {entry.clever_id}
                    </TableCell>
                    <TableCell className="capitalize">
                      {entry.entity_type || "unknown"}
                    </TableCell>
                    <TableCell>{describeRosterReason(entry.reason)}</TableCell>
                    <TableCell className="max-w-xs truncate text-xs text-muted-foreground">
                      {entry.details || "—"}
                    </TableCell>
                    <TableCell>{formatTimestamp(entry.last_seen_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </SectionState>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Publish failures
// ---------------------------------------------------------------------------

interface PublishFailuresTabProps {
  state: LoadState<AssignmentFailure[]>;
  onRedrive: (localId: number | string) => void;
  redrivingId: string | null;
  expandedId: string | null;
  onToggleExpanded: (id: string) => void;
}

function PublishFailuresTab({
  state,
  onRedrive,
  redrivingId,
  expandedId,
  onToggleExpanded,
}: PublishFailuresTabProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Publish failures</CardTitle>
        <CardDescription>
          Assignments that failed to publish to Clever classrooms.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <SectionState
          state={state}
          emptyTitle="No publish failures"
          emptyDescription="All recent assignment publishes have succeeded."
        >
          {(failures) => (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lesson</TableHead>
                  <TableHead>Classroom</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Last error</TableHead>
                  <TableHead>Last published</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {failures.map((failure) => {
                  const key = String(failure.local_id);
                  const expanded = expandedId === key;
                  return (
                    <TableRow key={key}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium text-foreground">
                            {failure.lesson_name || "(unnamed)"}
                          </span>
                          {failure.lesson_id != null ? (
                            <span className="text-xs text-muted-foreground">
                              Lesson #{failure.lesson_id}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span>{failure.classroom_name || "—"}</span>
                          {failure.classroom_id != null ? (
                            <span className="text-xs text-muted-foreground">
                              #{failure.classroom_id}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="capitalize">
                        {failure.publish_state || "unknown"}
                      </TableCell>
                      <TableCell className="max-w-sm">
                        {failure.last_sync_error ? (
                          <button
                            type="button"
                            onClick={() => onToggleExpanded(key)}
                            className="text-left text-xs text-muted-foreground hover:text-foreground"
                            title={
                              expanded
                                ? "Click to collapse"
                                : "Click to expand full error"
                            }
                          >
                            {expanded ? (
                              <pre className="whitespace-pre-wrap break-words">
                                {failure.last_sync_error}
                              </pre>
                            ) : (
                              truncateError(failure.last_sync_error)
                            )}
                          </button>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            —
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {formatTimestamp(failure.last_published_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onRedrive(failure.local_id)}
                          disabled={redrivingId === key}
                        >
                          <RotateCcw
                            className={cn(
                              "size-4",
                              redrivingId === key && "animate-spin",
                            )}
                          />
                          Re-drive
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </SectionState>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Gradeback failures
// ---------------------------------------------------------------------------

function GradebackFailuresTab({
  state,
}: {
  state: LoadState<SubmissionFailure[]>;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Gradeback failures</CardTitle>
        <CardDescription>
          Submissions whose scores/feedback could not be written back to Clever.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <SectionState
          state={state}
          emptyTitle="No gradeback failures"
          emptyDescription="All recent Clever gradeback writes have succeeded."
        >
          {(failures) => (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Student</TableHead>
                  <TableHead>Assignment</TableHead>
                  <TableHead>Payload</TableHead>
                  <TableHead>Last error</TableHead>
                  <TableHead>Last writeback</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {failures.map((failure, idx) => (
                  <TableRow
                    key={`${failure.clever_user_id}-${failure.submission_id ?? idx}`}
                  >
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-foreground">
                          {failure.student_name || failure.clever_user_id}
                        </span>
                        <span className="font-mono text-xs text-muted-foreground">
                          {failure.clever_user_id}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{failure.assignment_name || "—"}</TableCell>
                    <TableCell>
                      <details className="max-w-xs">
                        <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                          View JSON
                        </summary>
                        <pre className="mt-1 max-h-60 overflow-auto rounded-md bg-muted/40 p-2 text-[11px] leading-snug">
                          {formatPayload(failure.last_payload)}
                        </pre>
                      </details>
                    </TableCell>
                    <TableCell className="max-w-xs text-xs text-muted-foreground">
                      {truncateError(failure.last_sync_error)}
                    </TableCell>
                    <TableCell>
                      {formatTimestamp(failure.last_writeback_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </SectionState>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function CleverLmsAdmin() {
  const [activeTab, setActiveTab] = useState<TabValue>("districts");
  const [districts, setDistricts] = useState<LoadState<CleverDistrict[]>>(
    INITIAL_STATE,
  );
  const [roster, setRoster] = useState<LoadState<UnresolvedRosterEntry[]>>(
    INITIAL_STATE,
  );
  const [publishFailures, setPublishFailures] = useState<
    LoadState<AssignmentFailure[]>
  >(INITIAL_STATE);
  const [gradebackFailures, setGradebackFailures] = useState<
    LoadState<SubmissionFailure[]>
  >(INITIAL_STATE);

  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [redrivingId, setRedrivingId] = useState<string | null>(null);
  const [expandedError, setExpandedError] = useState<string | null>(null);

  const loadDistricts = useCallback(async () => {
    setDistricts(INITIAL_STATE);
    try {
      const items = await cleverLmsService.listDistricts();
      setDistricts(normalizeListState(items));
    } catch (error) {
      setDistricts(classifyError(error));
    }
  }, []);

  const loadRoster = useCallback(async () => {
    setRoster(INITIAL_STATE);
    try {
      const items = await cleverLmsService.listUnresolvedRoster();
      setRoster(normalizeListState(items));
    } catch (error) {
      setRoster(classifyError(error));
    }
  }, []);

  const loadPublishFailures = useCallback(async () => {
    setPublishFailures(INITIAL_STATE);
    try {
      const items = await cleverLmsService.listAssignmentFailures();
      setPublishFailures(normalizeListState(items));
    } catch (error) {
      setPublishFailures(classifyError(error));
    }
  }, []);

  const loadGradebackFailures = useCallback(async () => {
    setGradebackFailures(INITIAL_STATE);
    try {
      const items = await cleverLmsService.listSubmissionFailures();
      setGradebackFailures(normalizeListState(items));
    } catch (error) {
      setGradebackFailures(classifyError(error));
    }
  }, []);

  useEffect(() => {
    void loadDistricts();
    void loadRoster();
    void loadPublishFailures();
    void loadGradebackFailures();
  }, [
    loadDistricts,
    loadRoster,
    loadPublishFailures,
    loadGradebackFailures,
  ]);

  const handleRefreshDistrict = useCallback(
    async (districtId: string) => {
      setRefreshingId(districtId);
      try {
        const refreshed = await cleverLmsService.refreshDistrict(districtId);
        toast.success("Refreshed from Clever");
        setDistricts((current) => {
          if (current.status !== "ready") {
            return current;
          }
          const next = current.data.map((d) =>
            d.clever_district_id === districtId
              ? { ...d, ...refreshed }
              : d,
          );
          return { status: "ready", data: next };
        });
      } catch (error) {
        const message = isCleverLmsError(error)
          ? error.detail || error.message
          : error instanceof Error
            ? error.message
            : "Failed to refresh";
        toast.error(message);
      } finally {
        setRefreshingId(null);
      }
    },
    [],
  );

  const handleTriggerSync = useCallback(
    async (districtId: string) => {
      setSyncingId(districtId);
      try {
        await cleverLmsService.triggerRosterSync(districtId);
        toast.success("Roster sync enqueued");
      } catch (error) {
        const message = isCleverLmsError(error)
          ? error.detail || error.message
          : error instanceof Error
            ? error.message
            : "Failed to trigger sync";
        toast.error(message);
      } finally {
        setSyncingId(null);
      }
    },
    [],
  );

  const handleRedrive = useCallback(
    async (localId: number | string) => {
      const key = String(localId);
      setRedrivingId(key);
      try {
        await cleverLmsService.redriveAssignment(localId);
        toast.success("Re-drive enqueued");
        void loadPublishFailures();
      } catch (error) {
        const message = isCleverLmsError(error)
          ? error.detail || error.message
          : error instanceof Error
            ? error.message
            : "Failed to re-drive assignment";
        toast.error(message);
      } finally {
        setRedrivingId(null);
      }
    },
    [loadPublishFailures],
  );

  const handleToggleExpanded = useCallback((id: string) => {
    setExpandedError((current) => (current === id ? null : id));
  }, []);

  const tabDefinitions = useMemo(
    () =>
      [
        { value: "districts" as const, label: "Districts" },
        { value: "roster-issues" as const, label: "Roster issues" },
        { value: "publish-failures" as const, label: "Publish failures" },
        {
          value: "gradeback-failures" as const,
          label: "Gradeback failures",
        },
      ] satisfies { value: TabValue; label: string }[],
    [],
  );

  const handleReloadActive = useCallback(() => {
    switch (activeTab) {
      case "districts":
        void loadDistricts();
        return;
      case "roster-issues":
        void loadRoster();
        return;
      case "publish-failures":
        void loadPublishFailures();
        return;
      case "gradeback-failures":
        void loadGradebackFailures();
        return;
    }
  }, [
    activeTab,
    loadDistricts,
    loadRoster,
    loadPublishFailures,
    loadGradebackFailures,
  ]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto pr-1">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">Clever LMS Connect</h2>
          <p className="text-xs text-muted-foreground">
            Diagnostics for Clever OAuth, roster sync, assignment publishes and
            gradeback writes. Depends on ENGG-1766 endpoints.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={handleReloadActive}>
          <RefreshCw className="size-4" />
          Reload
        </Button>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as TabValue)}
        className="flex min-h-0 flex-1 flex-col gap-4"
      >
        <TabsList className="w-fit shrink-0 rounded-lg">
          {tabDefinitions.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent
          value="districts"
          className="m-0 data-[state=inactive]:hidden"
        >
          <DistrictsTab
            state={districts}
            onRefreshDistrict={handleRefreshDistrict}
            onTriggerSync={handleTriggerSync}
            refreshingId={refreshingId}
            syncingId={syncingId}
          />
        </TabsContent>

        <TabsContent
          value="roster-issues"
          className="m-0 data-[state=inactive]:hidden"
        >
          <RosterIssuesTab state={roster} />
        </TabsContent>

        <TabsContent
          value="publish-failures"
          className="m-0 data-[state=inactive]:hidden"
        >
          <PublishFailuresTab
            state={publishFailures}
            onRedrive={handleRedrive}
            redrivingId={redrivingId}
            expandedId={expandedError}
            onToggleExpanded={handleToggleExpanded}
          />
        </TabsContent>

        <TabsContent
          value="gradeback-failures"
          className="m-0 data-[state=inactive]:hidden"
        >
          <GradebackFailuresTab state={gradebackFailures} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Exposed for colocated tests.
// eslint-disable-next-line react-refresh/only-export-components
export { classifyError, normalizeListState };
